import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  Users,
  DollarSign,
  TrendingUp,
  UserPlus,
  LogOut,
  Building,
  PieChart,
  FileText,
  CheckCircle,
  AlertCircle,
  Copy,
  Link as LinkIcon,
  Clock,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import './AgentDashboard.css';

const AgentDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [businesses, setBusinesses] = useState([]);
  const [revenueReport, setRevenueReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [signature, setSignature] = useState('');
  const [agreementSigning, setAgreementSigning] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [commissions, setCommissions] = useState([]);
  const [businessForm, setBusinessForm] = useState({
    email: '',
    password: '',
    business_name: '',
    platform_fee_percentage: 1.0,
    pass_fee_to_customer: false
  });
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (user && !user.is_agent && user.role !== 'agent') {
      toast.error('Agent access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [businessesRes, revenueRes] = await Promise.all([
        axios.get(`${API}/agent/my-businesses`, { headers }),
        axios.get(`${API}/agent/revenue-report`, { headers })
      ]);
      
      setBusinesses(businessesRes.data);
      setRevenueReport(revenueRes.data);
      
      // Generate referral link
      const baseUrl = window.location.origin;
      setReferralLink(`${baseUrl}/auth?ref=${user?.id}`);
      
      // Fetch commissions if available
      try {
        const commissionsRes = await axios.get(`${API}/agent/commissions`, { headers });
        setCommissions(commissionsRes.data.commissions || []);
      } catch (e) {
        // Commissions endpoint may not exist yet for this agent.
        console.warn('Agent commissions fetch failed (non-blocking):', e?.message || e);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSignAgreement = async () => {
    if (!signature.trim()) {
      toast.error('Please type your full name as signature');
      return;
    }
    
    setAgreementSigning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/auth/sign-agreement`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          signature: signature.trim(),
          terms_version: '1.0'
        })
      });
      
      if (response.ok) {
        toast.success('Agreement signed successfully!');
        setShowAgreementModal(false);
        // Refresh user data
        window.location.reload();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to sign agreement');
      }
    } catch (error) {
      toast.error('Error signing agreement');
    } finally {
      setAgreementSigning(false);
    }
  };

  const handleRegisterBusiness = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/agent/register-business`, businessForm, { headers });
      
      toast.success('Business registered successfully!');
      setShowModal(false);
      setBusinessForm({
        email: '',
        password: '',
        business_name: '',
        platform_fee_percentage: 1.0,
        pass_fee_to_customer: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register business');
    }
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  // Check if agreement needs to be signed
  const needsAgreement = !user?.agreement_signed;

  return (
    <div className="agent-dashboard-page" data-testid="agent-dashboard">
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier</span>
            </div>
          </div>
          <div className="header-right">
            <div className="agent-badge-header">
              <Building size={16} />
              Agent Partner
            </div>
            <div className="user-info">
              <span className="user-name">{user?.business_name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-container">
          {/* Welcome Section */}
          <div className="page-header">
            <Building size={32} />
            <h1>Agent Partner Portal</h1>
            <p>Onboard businesses and track your commissions</p>
          </div>

          {/* Agreement Warning Banner */}
          {needsAgreement && (
            <div className="agreement-banner" data-testid="agreement-banner">
              <AlertCircle size={24} />
              <div className="banner-content">
                <h3>Partner Agreement Required</h3>
                <p>Please sign the partner agreement to start onboarding businesses and earning commissions.</p>
              </div>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAgreementModal(true)}
                data-testid="sign-agreement-btn"
              >
                <FileText size={18} /> Sign Agreement
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="dashboard-tabs">
            <button 
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <TrendingUp size={16} /> Overview
            </button>
            <button 
              className={`tab ${activeTab === 'businesses' ? 'active' : ''}`}
              onClick={() => setActiveTab('businesses')}
            >
              <Users size={16} /> My Businesses
            </button>
            <button 
              className={`tab ${activeTab === 'commissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('commissions')}
            >
              <DollarSign size={16} /> Commissions
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              {/* Revenue Stats */}
              {revenueReport && (
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon" style={{background: 'rgba(96, 165, 250, 0.1)'}}>
                      <Users size={24} color="#60a5fa" />
                    </div>
                    <div className="stat-info">
                      <p className="stat-label">Referred Businesses</p>
                      <p className="stat-value">{revenueReport.total_businesses}</p>
                    </div>
                  </div>

                  <div className="stat-card highlight">
                    <div className="stat-icon" style={{background: 'rgba(34, 197, 94, 0.1)'}}>
                      <DollarSign size={24} color="#22c55e" />
                    </div>
                    <div className="stat-info">
                      <p className="stat-label">Your Earnings</p>
                      <p className="stat-value">${revenueReport.agent_revenue?.toFixed(2) || '0.00'}</p>
                      <p className="stat-description">{revenueReport.revenue_share_percentage || 10}% commission rate</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon" style={{background: 'rgba(251, 191, 36, 0.1)'}}>
                      <TrendingUp size={24} color="#fbbf24" />
                    </div>
                    <div className="stat-info">
                      <p className="stat-label">Total Platform Fees</p>
                      <p className="stat-value">${revenueReport.total_platform_fees?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon" style={{background: 'rgba(168, 85, 247, 0.1)'}}>
                      <PieChart size={24} color="#a855f7" />
                    </div>
                    <div className="stat-info">
                      <p className="stat-label">Total Payment Volume</p>
                      <p className="stat-value">${revenueReport.total_payment_volume?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Referral Link Section */}
              <div className="referral-section">
                <h3><LinkIcon size={20} /> Your Referral Link</h3>
                <p className="referral-description">Share this link with businesses to sign them up under your agent account.</p>
                <div className="referral-link-box">
                  <input 
                    type="text" 
                    value={referralLink} 
                    readOnly 
                    className="referral-input"
                  />
                  <button className="btn btn-copy" onClick={copyReferralLink}>
                    <Copy size={16} /> Copy
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="action-section">
                <button 
                  className="btn btn-primary btn-large"
                  onClick={() => setShowModal(true)}
                  disabled={needsAgreement}
                  data-testid="register-business-btn"
                >
                  <UserPlus size={20} /> Register New Business
                </button>
                {needsAgreement && (
                  <p className="action-hint">Sign the partner agreement to register businesses</p>
                )}
              </div>
            </>
          )}

          {/* Businesses Tab */}
          {activeTab === 'businesses' && (
            <div className="businesses-section">
              <div className="section-header">
                <h2>My Referred Businesses</h2>
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowModal(true)}
                  disabled={needsAgreement}
                >
                  <UserPlus size={18} /> Register Business
                </button>
              </div>
              
              {businesses.length === 0 ? (
                <div className="empty-state">
                  <Users size={48} color="rgba(228, 230, 235, 0.3)" />
                  <p>No businesses registered yet</p>
                  <p className="empty-hint">Register your first business or share your referral link</p>
                </div>
              ) : (
                <div className="businesses-table-container">
                  <table className="businesses-table">
                    <thead>
                      <tr>
                        <th>Business Name</th>
                        <th>Email</th>
                        <th>Fee %</th>
                        <th>Transaction Volume</th>
                        <th>Platform Fees</th>
                        <th>Your Commission</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {businesses.map((business) => {
                        const agentEarnings = (business.platform_fees_accumulated || 0) * ((revenueReport?.revenue_share_percentage || 10) / 100);
                        return (
                          <tr key={business.user_id}>
                            <td className="business-name">{business.business_name}</td>
                            <td className="business-email">{business.email}</td>
                            <td>
                              <span className="fee-badge">{business.platform_fee_percentage}%</span>
                            </td>
                            <td>${business.total_transaction_volume?.toFixed(2) || '0.00'}</td>
                            <td className="platform-fees">${business.platform_fees_accumulated?.toFixed(2) || '0.00'}</td>
                            <td className="agent-earnings">${agentEarnings.toFixed(2)}</td>
                            <td>
                              <span className={`status-badge ${business.bank_verified ? 'verified' : 'pending'}`}>
                                {business.bank_verified ? (
                                  <><CheckCircle size={12} /> Verified</>
                                ) : (
                                  <><Clock size={12} /> Pending</>
                                )}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Commissions Tab */}
          {activeTab === 'commissions' && (
            <div className="commissions-section">
              <div className="section-header">
                <h2>Commission History</h2>
              </div>
              
              <div className="commission-summary">
                <div className="commission-card pending">
                  <Clock size={20} />
                  <div>
                    <span className="commission-label">Pending</span>
                    <span className="commission-value">$0.00</span>
                  </div>
                </div>
                <div className="commission-card approved">
                  <CheckCircle size={20} />
                  <div>
                    <span className="commission-label">Approved</span>
                    <span className="commission-value">$0.00</span>
                  </div>
                </div>
                <div className="commission-card paid">
                  <Send size={20} />
                  <div>
                    <span className="commission-label">Paid Out</span>
                    <span className="commission-value">${revenueReport?.agent_revenue?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>

              {commissions.length === 0 ? (
                <div className="empty-state">
                  <DollarSign size={48} color="rgba(228, 230, 235, 0.3)" />
                  <p>No commissions yet</p>
                  <p className="empty-hint">Commissions are generated when your referred businesses process payments</p>
                </div>
              ) : (
                <div className="commissions-table-container">
                  <table className="commissions-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Business</th>
                        <th>Payment Amount</th>
                        <th>Platform Fee</th>
                        <th>Your Commission</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions.map((commission) => (
                        <tr key={commission.id}>
                          <td>{new Date(commission.created_at).toLocaleDateString()}</td>
                          <td>{commission.merchant_name}</td>
                          <td>${commission.payment_amount?.toFixed(2)}</td>
                          <td>${commission.platform_fee?.toFixed(2)}</td>
                          <td className="commission-amount">${commission.commission_amount?.toFixed(2)}</td>
                          <td>
                            <span className={`status-badge ${commission.status}`}>
                              {commission.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Register Business Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              <UserPlus size={24} />
              Register New Business
            </h2>

            <form onSubmit={handleRegisterBusiness}>
              <div className="form-group">
                <label>Business Name *</label>
                <input
                  type="text"
                  value={businessForm.business_name}
                  onChange={(e) => setBusinessForm({...businessForm, business_name: e.target.value})}
                  placeholder="Enter business name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Business Email *</label>
                <input
                  type="email"
                  value={businessForm.email}
                  onChange={(e) => setBusinessForm({...businessForm, email: e.target.value})}
                  placeholder="business@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={businessForm.password}
                  onChange={(e) => setBusinessForm({...businessForm, password: e.target.value})}
                  placeholder="Minimum 6 characters"
                  minLength="6"
                  required
                />
              </div>

              <div className="form-group">
                <label>Platform Fee % (1-3%) *</label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={businessForm.platform_fee_percentage}
                  onChange={(e) => setBusinessForm({...businessForm, platform_fee_percentage: parseFloat(e.target.value)})}
                />
                <div className="range-value">{businessForm.platform_fee_percentage.toFixed(1)}%</div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={businessForm.pass_fee_to_customer}
                    onChange={(e) => setBusinessForm({...businessForm, pass_fee_to_customer: e.target.checked})}
                  />
                  <span>Pass fee to customer</span>
                </label>
              </div>

              <div className="revenue-info">
                <p>💰 Your Commission: <strong>{revenueReport?.revenue_share_percentage || 10}%</strong> of platform fees</p>
                <p className="help-text">
                  You'll earn {revenueReport?.revenue_share_percentage || 10}% of all platform fees from this business
                </p>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Register Business
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agreement Modal */}
      {showAgreementModal && (
        <div className="modal-overlay" onClick={() => setShowAgreementModal(false)}>
          <div className="modal-content agreement-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              <FileText size={24} />
              Partner Agreement
            </h2>

            <div className="agreement-content">
              <h3>Crypto Cashier Agent Partner Agreement</h3>
              
              <div className="agreement-text">
                <p><strong>1. Partnership Terms</strong></p>
                <p>By signing this agreement, you ("Agent") agree to partner with Crypto Cashier ("Company") to refer and onboard businesses to the platform.</p>
                
                <p><strong>2. Commission Structure</strong></p>
                <p>Agent will receive {revenueReport?.revenue_share_percentage || 10}% of all platform fees generated by businesses referred through your unique referral link or manually registered through your agent portal.</p>
                
                <p><strong>3. Payment Terms</strong></p>
                <p>Commissions are calculated based on completed payments. Payouts are processed monthly for approved commissions with a minimum threshold of $50.</p>
                
                <p><strong>4. Responsibilities</strong></p>
                <ul>
                  <li>Provide accurate information when registering businesses</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Not engage in fraudulent or misleading practices</li>
                  <li>Maintain confidentiality of business information</li>
                </ul>
                
                <p><strong>5. Termination</strong></p>
                <p>Either party may terminate this agreement with 30 days written notice. Earned commissions will be paid out upon termination.</p>
              </div>
            </div>

            <div className="signature-section">
              <label>Type your full name to sign *</label>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Your full legal name"
                className="signature-input"
              />
              <p className="signature-note">
                By typing your name above, you agree to the terms of this Partner Agreement.
              </p>
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setShowAgreementModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSignAgreement}
                disabled={agreementSigning || !signature.trim()}
              >
                {agreementSigning ? 'Signing...' : 'Sign Agreement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDashboard;
